import { Component, ChangeDetectionStrategy, inject, signal, DestroyRef } from '@angular/core';
import { Location } from '@angular/common';
import { Router, NavigationEnd, RouterLink } from '@angular/router';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { filter } from 'rxjs/operators';
import { ThemeService } from '../../core/services/theme.service';
import { AuthService } from '../../core/services/auth.service';

/**
 * Mobile app-shell top header (< 768px). On the homepage it shows the logo +
 * theme + profile; on sub-pages it shows a back chevron + a search shortcut.
 * It deliberately renders no title — each page provides its own <h1> (SEO).
 */
@Component({
  selector: 'app-mobile-header',
  imports: [RouterLink],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <header class="m-header">
      @if (isHome()) {
        <a routerLink="/" class="df-logo" style="flex:1; min-width:0;" aria-label="DateFirma — Acasă">
          <img src="/logo.webp" class="mark" alt="DateFirma.ro - logo director de firme din România" title="DateFirma.ro" width="30" height="30" />
          <span class="word">Date<strong>Firma</strong></span>
        </a>
        <button type="button" class="m-iconbtn" (click)="themeService.toggle()"
          [attr.aria-label]="themeService.isDark() ? 'Comută la modul luminos' : 'Comută la modul întunecat'">
          @if (themeService.isDark()) {
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M21 13A9 9 0 1 1 11 3a7 7 0 0 0 10 10z"/></svg>
          } @else {
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><circle cx="12" cy="12" r="4"/><path d="M12 2v2M12 20v2M4.9 4.9l1.4 1.4M17.7 17.7l1.4 1.4M2 12h2M20 12h2M4.9 19.1l1.4-1.4M17.7 6.3l1.4-1.4"/></svg>
          }
        </button>
        <a [routerLink]="authService.isLoggedIn() ? '/profil' : '/autentificare'" class="m-iconbtn" aria-label="Cont">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><circle cx="12" cy="8" r="4"/><path d="M4 21c0-4.4 3.6-8 8-8s8 3.6 8 8"/></svg>
        </a>
      } @else {
        <button type="button" class="m-iconbtn" (click)="goBack()" aria-label="Înapoi">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="m15 6-6 6 6 6"/></svg>
        </button>
        <span class="m-spacer"></span>
        <a routerLink="/cauta-firme" class="m-iconbtn" aria-label="Caută">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><circle cx="11" cy="11" r="7"/><path d="m20 20-3.5-3.5"/></svg>
        </a>
      }
    </header>
  `,
  styles: [`
    :host { display: block; position: sticky; top: 0; z-index: 40; background: var(--bg); }
    @media (min-width: 768px) { :host { display: none; } }
    .m-header {
      padding: calc(8px + env(safe-area-inset-top)) 16px 12px;
      display: flex; align-items: center; gap: 10px; min-height: 56px;
    }
    .m-header .m-spacer { flex: 1; }
    .m-iconbtn {
      width: 36px; height: 36px; border-radius: 50%; background: var(--surface); border: 1px solid var(--border);
      display: flex; align-items: center; justify-content: center; color: var(--text);
      flex-shrink: 0; cursor: pointer; text-decoration: none;
    }
  `],
})
export class MobileHeaderComponent {
  private readonly router = inject(Router);
  private readonly location = inject(Location);
  private readonly destroyRef = inject(DestroyRef);
  protected readonly themeService = inject(ThemeService);
  protected readonly authService = inject(AuthService);

  protected readonly isHome = signal(true);

  constructor() {
    this.update();
    this.router.events
      .pipe(filter((e) => e instanceof NavigationEnd), takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.update());
  }

  private update(): void {
    const path = this.router.url.split('?')[0].split('#')[0];
    this.isHome.set(path === '/' || path === '');
  }

  protected goBack(): void {
    this.location.back();
  }
}
